// React
import React, { useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
// Locales
import translate from 'locales/i18n';
// Material components
import Autocomplete from '@mui/material/Autocomplete';
import {
    Button,
    Table,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    TableBody,
    TableFooter,
    TablePagination,
    TableCell,
    Paper,
    Box
} from '@mui/material';

// Material Icons
import {
    AddBox,
    SearchRounded,
    Delete as DeleteIcon,
    Restore as RestoreIcon,
    Search as SearchIcon,
    FileCopy as FileCopyIcon,
    Language as LanguageIcon,
    Http as HttpIcon,
    Create as CreateIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';

// Stores
import LoggedUserStore from 'stores/LoggedUserStore';

// Services
import ProxyService from 'services/ProxyService';

// Dialogs
import DialogEditProxy from '../../dialogs/proxy/DialogEditProxy';

// Utils
import { countries, countryCodeToLabel, countryToFlag } from 'components/dialogs/proxy/DialogEditProxyUtils';
import AlertAction from 'actions/AlertAction';
import Loading from 'components/LoadingPage';
import DialogConfirm from 'components/dialogs/commons/DialogConfirm';
//Styles
import useStyles from 'components/page/ProxyPage/ProxiesManagerPage.style';

// Default value
const emptyProxy = {
    id: '',
    type: '',
    country: '',
    provider: '',
    host: '',
    login: '',
    pwd: ''
};

const emptyCountry = {
    'code': '',
    'label': '',
    'phone': ''
};

const ProxiesManager = () => {

    // Css
    const classes = useStyles();

    // Found domains
    const [domains, setDomains] = useState(null);
    const [countDomains, setCountDomains] = useState(0);

    // Pagination
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [page, setPage] = useState(1);
    // Search Fields
    const [provider, setProvider] = useState('');
    const [type, setType] = useState('');
    const [country, setCountry] = useState(emptyCountry);
    const [host, setHost] = useState('');

    // Dialogs
    const [openEdit, setOpenEdit] = useState(false);
    const [openCreate, setOpenCreate] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [currentProxy, setCurrentProxy] = useState(emptyProxy);

    // Everytime a search, or pagination change, reload.
    useEffect(() => {
        updateTable(rowsPerPage, page, provider, type, country, host);
    }, [rowsPerPage, page, provider, type, country, host]);

    // Services interactions
    const updateTable = () => {
        ProxyService.getDomains(rowsPerPage, page, provider, type, country.code, host)
            .then(result => {
                setDomains(result.proxies);
                setCountDomains(result.count);
            }, response => {
                AlertAction.open('error', translate.i18n(response && response.error ? response.error : 'ERROR_GET_TABLE'), 'top-right');
                setDomains([]);
            });
    };

    const updateProxy = (proxy) => {
        ProxyService.modifyDomain(proxy)
            .then(() => {
                resetParams();
                closeDialogs();
                AlertAction.open('success', translate.i18n('SUCCESS'), 'top-right');
            })
            .catch(() => {
                AlertAction.open('error', translate.i18n('FAILURE'), 'top-right');
            });
    };

    const createProxy = (proxy = emptyProxy) => {
        ProxyService.createDomain(proxy)
            .then(() => {
                resetParams();
                closeDialogs();
                AlertAction.open('success', translate.i18n('SUCCESS'), 'top-right');
            })
            .catch((err) => {
                err.then(res => {
                    let errorMessage = '';
                    if (res['obj.country']) errorMessage += translate.i18n('PROXY_MISSING_COUNTRY') + ' ';
                    if (errorMessage !== '') errorMessage += translate.i18n('AND') + ' ';
                    if (res['obj.type']) errorMessage += translate.i18n('PROXY_MISSING_TYPE') + ' ';
                    if (res.error) errorMessage += translate.i18n(res.error) + ' ';
                    AlertAction.open('error', errorMessage, 'top-right');
                });
            });
    };

    const deleteProxy = (id) => {
        ProxyService.deleteDomain(id)
            .then(() => {
                resetParams();
                AlertAction.open('success', translate.i18n('SUCCESS'), 'top-right');
            })
            .catch(() => {
                AlertAction.open('error', translate.i18n('FAILURE'), 'top-right');
            });

    };

    // State Interactions
    const resetParams = () => {
        setDomains(null);
        setPage(1);
        setRowsPerPage(10);
        setProvider('');
        setType('');
        setCountry(emptyCountry);
        setHost('');
        setCurrentProxy(emptyProxy);
        updateTable(10, 1, '', '', emptyCountry, '');
    };

    const search = () => {
        setDomains(null);
        setPage(1);
        updateTable(rowsPerPage, page, provider, type, country, host);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage + 1);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    const handleChangeSearch = (event, id = null, values = null) => {
        const targetId = id ? id : event.target.id;
        const targetValues = values ? values : event.target.value;
        if (targetId === 'providerSearch') setProvider(targetValues ? targetValues : '');
        else if (targetId === 'typeSearch') setType(targetValues ? targetValues : '');
        else if (targetId === 'countrySearch') setCountry(targetValues ? targetValues : emptyCountry);
        else if (targetId === 'hostSearch') setHost(targetValues ? targetValues : '');
    };

    const headCells = [
        { id: 'hostname', disablePadding: false, label: translate.i18n('HOST'), align: 'left' },
        { id: 'type', disablePadding: false, label: translate.i18n('TYPE'), align: 'left' },
        { id: 'provider', disablePadding: false, label: translate.i18n('PROVIDER'), align: 'left' },
        { id: 'country', disablePadding: false, label: translate.i18n('COUNTRY'), align: 'left' },
        { id: 'duplicate', disablePadding: false, label: translate.i18n('DUPLICATE'), align: 'right' },
        { id: 'modify', disablePadding: false, label: translate.i18n('MODIFY'), align: 'right' },
        { id: 'delete', disablePadding: false, label: translate.i18n('DELETE'), align: 'right' },
    ];

    // Event interactions
    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            search();
        }
    };

    const closeDialogs = () => {
        setOpenEdit(false);
        setOpenCreate(false);
        setOpenDelete(false);
        setCurrentProxy(emptyProxy);
    };

    const handleModify = (proxy) => {
        setCurrentProxy(proxy);
        setOpenEdit(true);
    };

    const handleCreate = (proxy = emptyProxy) => {
        setCurrentProxy(proxy);
        setOpenCreate(true);
    };

    const handleDuplicate = (proxy) => {
        const tempProxy = proxy;
        tempProxy.id = '';
        setCurrentProxy(tempProxy);
        setOpenCreate(true);
    };

    const handleDelete = (proxy) => {
        setCurrentProxy(proxy);
        setOpenDelete(true);
    };

    // Renders
    const renderDialogEditProxy = () =>
        <DialogEditProxy
            close={closeDialogs}
            open={openEdit}
            proxy={currentProxy}
            callbackFunction={updateProxy}
            title={translate.i18n('EDIT_PROXY')}
        />
        ;

    const renderDialogCreateProxy = () =>
        <DialogEditProxy
            close={closeDialogs}
            open={openCreate}
            proxy={currentProxy}
            callbackFunction={createProxy}
            title={translate.i18n('ADD_PROXY')}
        />
        ;

    const renderDialogDeleteProxy = () =>
        <DialogConfirm
            close={closeDialogs}
            open={openDelete}
            callback={() => deleteProxy(currentProxy.id)}
            title={translate.i18n('DELETE_PROXY')}
            text={[
                `${translate.i18n('HOST')} :  ${currentProxy.host}`,
                `${translate.i18n('TYPE')} : ${currentProxy.type}`,
                `${translate.i18n('PROVIDER')}  : ${currentProxy.provider}`
            ]}
        />
        ;

    // Login protection
    if (!LoggedUserStore.isLogged()) {
        return (
            <Redirect to='/login' />
        );
    }

    // Page
    return <div className={classes.root}>
        <Paper className={classes.paper}>
            <div className={classes.searchPartContainer}>
                <div className={classes.searchBarContainer}>
                    <div className={classes.searchIcon}>
                        <SearchRounded />
                    </div>
                    <div className={classes.searchBar}>
                        <TextField
                            id='providerSearch'
                            label={translate.i18n('PROVIDER')}
                            fullWidth
                            variant='outlined'
                            value={provider}
                            onChange={handleChangeSearch}
                            onKeyPress={handleKeyPress}
                        />
                    </div>
                </div>
                <div className={classes.searchBarContainer}>
                    <div className={classes.searchIcon}>
                        <SettingsIcon />
                    </div>
                    <div className={classes.searchBar}>
                        <Autocomplete
                            className={classes.textField}
                            defaultValue={type}
                            onChange={(event, newValue) => {
                                handleChangeSearch(event, 'typeSearch', newValue);
                            }}
                            id='type'
                            options={['DATACENTER', 'MOBILE', 'RESIDENTIAL', 'TOR', 'TRANSPARENT']}
                            renderInput={(params) =>
                                <TextField {...params} label={translate.i18n('TYPE')} variant='outlined' />
                            }
                        />
                    </div>
                </div>
                <div className={classes.searchBarContainer}>
                    <div className={classes.searchIcon}>
                        <LanguageIcon />
                    </div>
                    <div className={classes.searchBar}>
                        <Autocomplete
                            id='countrySearch'
                            classes={{
                                root: classes.textField
                            }}
                            options={countries}
                            autoHighlight
                            value={country}
                            onChange={(event, values) => handleChangeSearch(event, 'countrySearch', values)}
                            getOptionLabel={(option) => option.label}
                            renderOption={(props, option) => <Box component='li' sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                <img
                                    loading='lazy'
                                    width='20'
                                    src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                                    srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                                    alt=''
                                />
                                {option.label} ({option.code})
                            </Box>
                            }
                            renderInput={(params) => <TextField
                                {...params}
                                label={translate.i18n('CHOOSE_COUNTRY')}
                                variant='outlined'
                            />
                            }
                        />
                    </div>
                </div>
                <div className={classes.searchBarContainer}>
                    <div className={classes.searchIcon}>
                        <HttpIcon />
                    </div>
                    <div className={classes.searchBar}>
                        <TextField
                            id='hostSearch'
                            label={translate.i18n('HOST')}
                            fullWidth
                            variant='outlined'
                            value={host}
                            onChange={handleChangeSearch}
                            onKeyPress={handleKeyPress}
                        />
                    </div>
                </div>
                <div className={classes.searchButtonContainer}>
                    <Button
                        variant='contained'
                        classes={{
                            label: classes.buttonLabel
                        }}
                        color='secondary'
                        className={classes.addButton}
                        endIcon={<RestoreIcon />}
                        onClick={resetParams}
                        disabled={!domains}
                    >
                        {translate.i18n('RESET')}
                    </Button>
                    <Button
                        variant='contained'
                        classes={{
                            label: classes.buttonLabel
                        }}
                        className={classes.addButton}
                        endIcon={<SearchIcon />}
                        onClick={search}
                        disabled={!domains}
                    >
                        {translate.i18n('RESEARCH')}
                    </Button>
                </div>
                <div className={classes.searchButtonContainer}>
                    <Button
                        variant='contained'
                        color='primary'
                        classes={{
                            label: classes.buttonLabel
                        }}
                        className={classes.addButton}
                        endIcon={<AddBox />}
                        onClick={() => handleCreate()}
                        disabled={!domains}
                    >
                        {translate.i18n('ADD_PROXY')}
                    </Button>
                </div>
            </div>
            {
                domains
                    ?
                    <TableContainer component={Paper} className={classes.tableContainer}>
                        <div className={classes.proxiesContainer}>
                            <Table stickyHeader size='small'>
                                <TableHead>
                                    <TableRow>
                                        {headCells.map((el) =>
                                            <TableCell
                                                align={el.align}
                                                key={el.id}>{el.label}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody className={classes.tableBody}>
                                    {
                                        domains.map((row) => {
                                            const canBeEdit = !['TRANSPARENT', 'TOR'].some(el => el === row.type);
                                            return (
                                                <TableRow key={row.id}>
                                                    <TableCell align='left'>
                                                        {row.host}
                                                    </TableCell>
                                                    <TableCell align='left'>
                                                        {row.type}
                                                    </TableCell>
                                                    <TableCell align='left'>
                                                        {row.provider}
                                                    </TableCell>
                                                    <TableCell
                                                        title={countryCodeToLabel(row.country)}
                                                        align='left'
                                                    >
                                                        <>
                                                            <span
                                                                className={classes.cellCountryFlag}
                                                            >
                                                                {countryToFlag(row.country)}
                                                            </span>
                                                        </>
                                                    </TableCell>
                                                    <TableCell
                                                        align='right'
                                                        className={canBeEdit && classes.iconButton}
                                                        onClick={() => {
                                                            canBeEdit && handleDuplicate(row);
                                                        }}
                                                    >
                                                        <FileCopyIcon color={canBeEdit ? 'primary' : 'disabled'} />
                                                    </TableCell>
                                                    <TableCell
                                                        align='right'
                                                        className={canBeEdit && classes.iconButton}
                                                        onClick={() => canBeEdit && handleModify(row)}
                                                    >
                                                        <CreateIcon color={canBeEdit ? 'primary' : 'disabled'} />
                                                    </TableCell>
                                                    <TableCell
                                                        align='right'
                                                        className={canBeEdit && classes.iconButton}
                                                        onClick={() => canBeEdit && handleDelete(row)}
                                                    >
                                                        <DeleteIcon color={canBeEdit ? 'primary' : 'disabled'} />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    }
                                </TableBody>
                            </Table>
                        </div>
                        <Table className={classes.tableFooter}>
                            <TableFooter>
                                <TableRow>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                                        count={countDomains}
                                        rowsPerPage={rowsPerPage}
                                        page={page - 1}
                                        SelectProps={{
                                            inputProps: { 'aria-label': 'rows per page' },
                                            native: true,
                                        }}
                                        onChangePage={handleChangePage}
                                        onChangeRowsPerPage={handleChangeRowsPerPage}
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                    :
                    <div className={classes.loaderContainer}>
                        <Loading />
                    </div>
            }
        </Paper>
        {renderDialogEditProxy()}
        {renderDialogCreateProxy()}
        {renderDialogDeleteProxy()}
    </div>;

};

export default ProxiesManager;
